import React from 'react'
import { Create, useForm, useSelect } from '@refinedev/antd'
import { Form, Input, Select, Upload, message } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import type { UploadRequestOption } from 'rc-upload/lib/interface'
import type { Tables } from '@muaa/shared'
import { supabaseClient } from '../../lib/supabaseClient'

type Photo = Tables<'photos'>
type Album = Tables<'albums'>

export function PhotoCreate() {
  const { formProps, saveButtonProps, form } = useForm<Photo>()

  const { selectProps: albumSelectProps } = useSelect<Album>({
    resource: 'albums',
    optionLabel: 'name',
    optionValue: 'id',
    pagination: { mode: 'off' },
  })

  const handleUpload = async (options: UploadRequestOption) => {
    const { file, onSuccess, onError } = options
    const uploadFile = file as File
    const ext = uploadFile.name.split('.').pop() ?? 'jpg'
    const path = `uploads/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`

    const { error } = await supabaseClient.storage
      .from('photos')
      .upload(path, uploadFile, { upsert: false })

    if (error) {
      message.error(`Upload failed: ${error.message}`)
      onError?.(error)
      return
    }

    const { data: urlData } = supabaseClient.storage
      .from('photos')
      .getPublicUrl(path)

    const publicUrl = urlData.publicUrl
    form.setFieldValue('url', publicUrl)
    message.success('Image uploaded')
    onSuccess?.(publicUrl)
  }

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Form.Item
          label="Album"
          name="album_id"
          rules={[{ required: true, message: 'Album is required' }]}
        >
          <Select
            {...albumSelectProps}
            placeholder="Select album"
            showSearch
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          label="Label"
          name="label"
          rules={[{ required: true, message: 'Label is required' }]}
        >
          <Input placeholder="e.g. group_photo_2025" />
        </Form.Item>

        <Form.Item label="Upload Image">
          <Upload.Dragger
            accept="image/*"
            customRequest={handleUpload}
            maxCount={1}
            showUploadList={{ showRemoveIcon: false }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag image to upload</p>
            <p className="ant-upload-hint">Uploads to Supabase Storage (photos bucket)</p>
          </Upload.Dragger>
        </Form.Item>

        {/* Hidden field that holds the Storage public URL after upload */}
        <Form.Item name="url" hidden>
          <Input />
        </Form.Item>
      </Form>
    </Create>
  )
}
